import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import { api, download } from '../api';
import ProductionDialog from '../ProductionDialog';
import { can, statusLabels } from '@zilian/contracts';
import {
  amount,
  date,
  Status,
  Empty,
  FitTableContainer,
  Heading,
  Props,
  QTY_DECIMALS,
  usePagination,
} from '../ui';

// 库存余额 / 库存流水 / 仓库: the warehouses live with the stock they hold, kept by the
// administrator and the warehouse keeper (not in 企业设置, which the warehouse never opens).
const WAREHOUSES = 2;

export function Inventory(p: Props) {
  const [tab, setTab] = useState(0),
    [data, setData] = useState<any[]>([]),
    // false: closed; otherwise the product to start from, if any.
    [producing, setProducing] = useState<string | null | false>(false);
  const skus: any[] = p.bootstrap?.skus ?? [];
  // Only an enterprise with items it makes (not only buys) gets the production entry.
  const makes = skus.some((s) => s.usage !== 'PURCHASE');
  const recipes: any[] = p.bootstrap?.recipes ?? [];
  const roles: string[] = p.current?.roles ?? [];
  const keepsWarehouses = roles.some((r) => r === 'admin' || r === 'warehouse');
  const { pageItems, pager } = usePagination(data, String(tab));
  const addWarehouse = () =>
    p.open({
      title: '新增仓库',
      fields: [{ name: 'name', label: '仓库名称', helper: '如：成品二号仓、原料仓' }],
      form: { name: '' },
      confirmLabel: '新增',
      submit: (f: any) => {
        if (!String(f.name ?? '').trim()) throw new Error('请填写仓库名称');
        return api('/warehouses', { name: f.name.trim() });
      },
    });
  useEffect(() => {
    if (tab === WAREHOUSES) return;
    let live = true;
    api<any[]>(tab ? '/ledger' : '/inventory')
      .then((d) => {
        if (live) setData(d);
      })
      .catch((e) => p.setError(e.message));
    return () => {
      live = false;
    };
  }, [tab, p.version, p.workspace]);
  // From a balance row the item is fixed (named in the description, not a locked select);
  // warehouse and status start from that row but stay editable.
  const adjust = (row?: any) =>
    p.open({
      title: row ? '调整库存' : '库存期初 / 盘点调整',
      description: `${row ? `商品：${row.name}（${row.code}）。` : ''}调整形成独立流水，不能覆盖历史余额。减少数量填写负数；必须记录真实原因。`,
      fields: [
        { name: 'warehouseId', label: '仓库', options: p.warehouses },
        ...(row ? [] : [{ name: 'skuId', label: '商品', skus }]),
        {
          name: 'delta',
          label: '增减数量',
          decimals: QTY_DECIMALS,
          signed: true,
          unit: (f: any) => skus.find((s) => s.id === (row?.sku_id ?? f.skuId))?.unit,
        },
        {
          name: 'quality',
          label: '库存状态',
          options: [
            { value: 'AVAILABLE', label: '合格可用' },
            { value: 'QUARANTINE', label: '待检' },
          ],
        },
        {
          name: 'reason',
          label: '调整原因',
          wide: true,
          // Increases first, then decreases; anything else can be typed.
          suggestions: [
            '期初建账',
            '盘点盘盈',
            '盘点盘亏',
            '运输破损',
            '仓储损坏',
            '报废 / 过期',
            '样品 / 赠送',
          ],
          helper: '可选常用原因，也可直接输入；减少数量请填写负数',
        },
      ],
      form: {
        warehouseId: row?.warehouse_id ?? p.warehouses[0]?.value,
        skuId: row?.sku_id ?? '',
        delta: '',
        quality: row?.quality ?? 'AVAILABLE',
        reason: '',
      },
      // Made goods added here leave their materials untouched; a production receipt takes them.
      notice: (f: any) => {
        const skuId = row?.sku_id ?? f.skuId;
        if (
          !(Number(f.delta) > 0) ||
          !recipes.some((r) => r.product_id === skuId && r.kind === 'PRODUCE')
        )
          return null;
        return {
          text: '生产出的成品请用「生产入库」，会按用料同时扣减原料。',
          action: { label: '去生产入库', run: () => setProducing(skuId) },
        };
      },
      submit: (f: any, key: string) => {
        if (!row && !f.skuId) throw new Error('请选择商品');
        const delta = String(f.delta ?? '').replace(/^\+/, '');
        if (!/^-?\d+(\.\d+)?$/.test(delta) || Number(delta) === 0)
          throw new Error('请填写增减数量：增加直接填数字，减少在数字前加 -');
        return api('/inventory/adjust', { ...f, delta, key });
      },
    });
  return (
    <>
      <Heading
        title="库存"
        subtitle="实际库存、订单占用与每一次增减，均可追溯。"
        action={
          <>
            <Button
              variant="outlined"
              startIcon={<DownloadOutlined />}
              onClick={() => p.safe(() => download('/export?kind=inventory', '库存.csv'))}
            >
              导出库存
            </Button>
            <Button
              variant={makes ? 'outlined' : 'contained'}
              startIcon={<Add />}
              onClick={() => adjust()}
            >
              期初 / 调整
            </Button>
            {makes && (
              <Button variant="contained" startIcon={<Add />} onClick={() => setProducing(null)}>
                生产入库
              </Button>
            )}
          </>
        }
      />
      <Paper>
        {/* The open tab's own action sits at the right of the tab bar. */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', px: 2, borderBottom: '1px solid #e7edef' }}
        >
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label="库存余额" />
            <Tab label="库存流水" />
            <Tab label="仓库" />
          </Tabs>
          {tab === WAREHOUSES && keepsWarehouses && (
            <Button startIcon={<Add />} onClick={addWarehouse} sx={{ ml: 'auto' }}>
              新增仓库
            </Button>
          )}
        </Box>
        {tab === WAREHOUSES ? (
          <Warehouses warehouses={p.bootstrap?.warehouses ?? []} />
        ) : !data.length ? (
          <Empty />
        ) : (
          <>
            <FitTableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {(tab
                      ? ['时间', '商品', '仓库', '状态', '变动数量', '来源', '原因']
                      : ['商品 / 规格', '仓库', '状态', '实物数量', '已占用', '可用数量', '操作']
                    ).map((h) => (
                      <TableCell key={h}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageItems.map((r, i) => (
                    <TableRow key={r.id ?? i}>
                      {tab === 1 && <TableCell>{date(r.created_at)}</TableCell>}
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 550,
                          }}
                        >
                          {r.name}
                        </Typography>
                        <Typography
                          color="text.secondary"
                          sx={{
                            fontSize: 11,
                          }}
                        >
                          {r.code}
                          {r.spec ? ` · ${r.spec}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>{r.warehouse_name}</TableCell>
                      <TableCell>
                        <Status value={r.quality} />
                      </TableCell>
                      {tab ? (
                        <>
                          <TableCell
                            sx={{
                              color: Number(r.delta) > 0 ? 'primary.main' : '#b45b47',
                              fontWeight: 650,
                            }}
                          >
                            {Number(r.delta) > 0 ? '+' : ''}
                            {amount(r.delta)} {r.unit}
                          </TableCell>
                          <TableCell>{statusLabels[r.source_type] ?? r.source_type}</TableCell>
                          <TableCell>{r.reason || '—'}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            {amount(r.on_hand)} {r.unit}
                          </TableCell>
                          <TableCell>{amount(r.reserved)}</TableCell>
                          <TableCell sx={{ fontWeight: 650, color: 'primary.main' }}>
                            {r.quality === 'AVAILABLE' ? amount(r.available) : '不可分配'}
                          </TableCell>
                          {/* Pulled in by the small text button's 5px padding so its text lines up
                            with the 操作 header, as in the item list. */}
                          <TableCell sx={{ pl: '11px' }}>
                            <Button size="small" onClick={() => adjust(r)}>
                              调整库存
                            </Button>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </FitTableContainer>
            {pager && <Box sx={{ borderTop: '1px solid #e7edef' }}>{pager}</Box>}
          </>
        )}
      </Paper>
      {producing !== false && (
        <ProductionDialog
          productId={producing}
          skus={skus}
          recipes={p.bootstrap?.recipes ?? []}
          categories={p.bootstrap?.categories ?? []}
          warehouses={p.warehouses}
          onChanged={p.reload}
          canEdit={can(roles, 'products')}
          onClose={() => setProducing(false)}
          onDone={(message) => {
            setProducing(false);
            p.notify(message);
            p.reload();
          }}
        />
      )}
    </>
  );
}

/** The enterprise's warehouses. */
function Warehouses(p: { warehouses: { id: string; name: string }[] }) {
  if (!p.warehouses.length) return <Empty>还没有仓库，新增一个后即可登记收发货。</Empty>;
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>仓库名称</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {p.warehouses.map((w) => (
          <TableRow key={w.id}>
            <TableCell>{w.name}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
